import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createBusinessPartnerPlan,
  getBusinessPartnerPlansLinesListWithoutPagination,
  getPlanListByAssignedLines,
  getDefaultCommissionEnums,
} from './queries';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { isEmpty } from '@utils/validationUtils';
import { getError } from '@utils/graph-utils';
import {
  extractLines,
  extractPlans,
  extractSelectedPlanByLine,
  getBusinessPartnerLineDetails,
  getDataFromSeletedPlans,
  LookupToList,
} from './utils';
import { trimTrailingZeros } from '@utils/formatting-utils';
import PrecisionService from '@services/PrecisionService';
import DateService from '@services/DateService';
import { IAbstractRecord } from '@models';
import { IDetailedSelection } from '../../../..';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { cloneDeep } from 'lodash';

interface IAssignPlanDrawerProps {
  businessPartnerId: string;
  isDirectSalesforce: boolean;
  assignedLineId?: string;
  selectedBusinessPlanIds: string[];
  selectedDetailedSelection: IDetailedSelection[];
  open?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignPlanDrawer: React.FC<IAssignPlanDrawerProps> = ({
  businessPartnerId,
  isDirectSalesforce,
  assignedLineId,
  selectedBusinessPlanIds,
  selectedDetailedSelection,
  open,
  onSuccess,
  onClose,
}) => {
  const [createBusinessPartnerPlanAction] = useMutation(
    createBusinessPartnerPlan()
  );
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [values, setValues] = useState<IAbstractRecord>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    line: {},
    plan: {},
    commissionType: {},
    ValueCurrency: {},
    ApplicableOn: {},
  });

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(values, lovs, isDirectSalesforce)
  );

  const [getBusinessPartnerLinesListWithoutPaginationLazy, businessLineData] =
    useLazyQuery(getBusinessPartnerPlansLinesListWithoutPagination());
  const [getPlanListByAssignedLinesLazy] = useLazyQuery(
    getPlanListByAssignedLines
  );

  const [getDefaultCommissionEnumsLazy] = useLazyQuery(
    getDefaultCommissionEnums()
  );

  const initialize = async () => {
    setLoading(true);

    const businessPartnerLines =
      await handleBusinessPartnerSelection(businessPartnerId);

    const commissionEnumsData = await getDefaultCommissionEnumsLazy();

    const newDefualtCommissionEnums = LookupToList(commissionEnumsData.data);

    const newLovs: Record<string, Record<string, string>> = {
      line: businessPartnerLines.result,
      lineMapping: businessPartnerLines.partnerLineDictionary,
      plan: {},
      commissionType: newDefualtCommissionEnums['commissionTypes'] || {},
      ApplicableOn:
        newDefualtCommissionEnums['commissionApplicableOnOptions'] || {},
    };

    if (assignedLineId) {
      const plans = await handleLineSelection(assignedLineId);
      newLovs['plan'] = plans;
      setValues((prevValues) => ({
        ...prevValues,
        line: assignedLineId,
        plan: selectedBusinessPlanIds || [],
        ...selectedDetailedSelection,
      }));
    }

    setLovs(newLovs);
    setLoading(false);
    setInputsForm(drawerSections(values, newLovs, isDirectSalesforce));
  };

  useEffect(() => {
    initialize();
  }, [businessPartnerId]);

  const handleBusinessPartnerSelection = async (selectedOption: string) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getBusinessPartnerLinesListWithoutPaginationLazy({
        fetchPolicy: 'no-cache',
        variables: {
          SelectedStatuses: ['ACTIVE'],
          selectedBusinessPartnerID: businessPartnerId,
        },
      });
      return extractLines(newData.data);
    }
    return { result: {}, partnerLineDictionary: {} };
  };

  const [plansData, setPlansData] = useState<IAbstractRecord>();
  const handleLineSelection = async (selectedOption: string) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getPlanListByAssignedLinesLazy({
        variables: {
          selectedBusinessPartnerID: businessPartnerId,
          selectedLineID: selectedOption,
          SelectedStatuses: ['ACTIVE'],
        },
        errorPolicy: 'all',
      });
      setPlansData(newData.data);
      return extractPlans(newData.data);
    }
    return {} as Record<string, string>;
  };

  const onCustomChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    allValues: IAbstractRecord,
    errors: Record<string, string>
  ) => {
    const newLovs = { ...lovs };
    if (fieldName === 'line') {
      setFormDisabled(true);
      newLovs.plan = await handleLineSelection(value);
    }

    if (fieldName === 'plan') {
      const newListCount = value?.length || 0;

      if (plansData && newListCount == 1) {
        const selectedOptionData =
          plansData.SalesforceManagement.queries.GetPlanListByAssignedLines.find(
            (item: IAbstractRecord) =>
              item.planConfigManagement_Plan_Id === value[0]
          );
        const selectedPlanData = extractSelectedPlanByLine(
          lovs?.lineMapping[allValues.line] || '',
          selectedOptionData
        );

        const lineDetails = getBusinessPartnerLineDetails(
          businessLineData.data,
          allValues.line
        );
        const { type, rate, commissionValue, valueCurrency, applicableOn } =
          getDataFromSeletedPlans(selectedPlanData);

        allValues.commissionType = type || lineDetails?.type;
        allValues.lineCommissionValue = commissionValue || lineDetails?.value;
        allValues.lineCommissionRate = rate || lineDetails?.rate;
        allValues.ValueCurrency = valueCurrency || lineDetails?.currency;
        allValues.ApplicableOn = applicableOn || lineDetails?.applicableOn;
        allValues.activeFrom =
          selectedOptionData.planConfigManagement_Plan_EffectiveFrom;
        allValues.activeTo =
          selectedOptionData.planConfigManagement_Plan_EffectiveTo;

        errors.commissionType = '';
        errors.lineCommissionRate = '';
        errors.ValueCurrency = '';
        errors.ApplicableOn = '';
        errors.activeFrom = '';
        errors.activeTo = '';
        errors.lineCommissionValue = '';
      } else {
        allValues.commissionType = 'RATE';
        allValues.lineCommissionValue = '';
        allValues.lineCommissionRate = '';
        allValues.ValueCurrency = '';
        allValues.ApplicableOn = '';
        allValues.activeFrom = '';
        allValues.activeTo = '';

        errors.commissionType = '';
        errors.lineCommissionRate = '';
        errors.ValueCurrency = '';
        errors.ApplicableOn = '';
        errors.activeFrom = '';
        errors.activeTo = '';
        errors.lineCommissionValue = '';
      }
    }

    if (fieldName === 'commissionType') {
      const isRate = value.toLowerCase() === 'rate';
      if (isRate) {
        allValues['lineCommissionValue'] = '';
        allValues['ValueCurrency'] = '';
      } else {
        allValues['lineCommissionRate'] = '';
      }
    }

    if (fieldName === 'activeFrom' || fieldName === 'activeTo') {
      if (isEmpty(value)) {
        errors[fieldName] = 'Required';
      } else {
        errors[fieldName] = '';
      }
    }

    setValues(allValues);
    setLovs(newLovs);
    setFormDisabled(false);
    setInputsForm(drawerSections(allValues, newLovs, isDirectSalesforce));
  };

  const submitForm = async (values: IAbstractRecord) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(values, lovs, isDirectSalesforce),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const isRate = data.commissionType.toLowerCase() === 'rate';

      const variables = {
        selectedPlanIDs: data.plan || [],
        businessPartnerLineID: lovs?.lineMapping[data.line] || '',
        businessPartnerPlanInputs: {
          activeFrom: DateService.formatDateBackend(data.activeFrom),
          activeTo: DateService.formatDateBackend(data.activeTo),
          planCommissionApplicableOn: data.ApplicableOn || null,
          planCommissionRate: isRate
            ? PrecisionService.divideBy100(data.lineCommissionRate)
            : 0,
          planCommissionType: data.commissionType,
          planCommissionValue: !isRate
            ? trimTrailingZeros(data.lineCommissionValue)
            : 0,
          planCommissionValueCurrency: !isRate ? data.ValueCurrency : '39735',
        },
      };

      const response = await createBusinessPartnerPlanAction({
        variables: { ...variables, entityId: businessPartnerId },
        errorPolicy: 'all',
      });

      if (isEmpty(response.errors)) {
        toast.success(
          <ToastSuccessMessage>
            Plan(s) successfully assigned.
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>{getError(response)}</ToastErrorMessage>
        );
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Add Plan(s)'}
      onClose={() => onClose()}
      isOpen={open}
      loading={loading}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      onChange={onCustomChange}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      noteSection={{
        text: "Note that plans' preset special commissions will override the commissions entered in this form.",
      }}
    />
  );
};

export default AssignPlanDrawer;
